import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";

export type UserRole = "fournisseur" | "supplier_guest" | "kam" | "chef_rayon" | "chef_secteur_magasin" | "admin_magasin" | "admin_fournisseur" | "admin" | "admin_centrale" | "collaborateur_magasin" | null;

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string;
  store_id: string | null;
  department_id: string | null;
  enterprise_id: string | null;
  region_id: string | null;
  centrale_id: string | null;
  supplier_title: string | null;
  is_active: boolean;
  trial_expires_at: string | null;
  created_via: string | null;
  job_function_id: string | null;
  job_function_label: string | null;
  job_title: string | null;
  supplier_account_type: string | null;
  data_consent_accepted_at: string | null;
  data_consent_version: string | null;
  data_consent_source: string | null;
}

// Priorité STRICTE : tous les rôles magasin AVANT tout rôle fournisseur,
// pour qu'un utilisateur magasin ne soit jamais affiché comme fournisseur
// même s'il porte un rôle fournisseur résiduel.
const ROLE_PRIORITY: Exclude<UserRole, null>[] = [
  "admin",
  "admin_centrale",
  "admin_magasin",
  "chef_secteur_magasin",
  "chef_rayon",
  "collaborateur_magasin",
  "admin_fournisseur",
  "kam",
  "fournisseur",
  "supplier_guest",
];

function pickHighestRole(rows: Array<{ role: string }>): UserRole {
  const roles = rows.map((row) => row.role as UserRole).filter(Boolean) as Exclude<UserRole, null>[];
  return roles.sort((a, b) => ROLE_PRIORITY.indexOf(a) - ROLE_PRIORITY.indexOf(b))[0] ?? null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  userRole: UserRole;
  userProfile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
}

interface AuthContextType extends AuthState {
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Simple timeout wrapper
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timeoutId: number | undefined;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(`Timeout after ${ms}ms: ${label}`)), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    userRole: null,
    userProfile: null,
    isLoading: true,
    isAuthenticated: false,
    isLoggingOut: false,
  });

  const currentUserIdRef = useRef<string | null>(null);
  const isLoggingOutRef = useRef(false);
  const queryClient = useQueryClient();

  // Fetch role with retry
  const fetchUserRole = useCallback(async (userId: string): Promise<UserRole> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data, error } = await supabase.rpc("get_user_role", { _user_id: userId });
        if (!error && data) return data as UserRole;
        
        if (attempt === 0) {
          console.warn("[AuthContext] Role RPC failed, retrying...", error?.message);
          await new Promise(r => setTimeout(r, 500));
          continue;
        }
        
        // Final fallback: query table directly
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role, created_at")
          .eq("user_id", userId)
          .order("created_at", { ascending: true });
        return pickHighestRole(roleData || []);
      } catch (e) {
        if (attempt === 1) console.error("[AuthContext] Role fetch failed:", e);
      }
    }
    return null;
  }, []);

  // Fetch profile
  const fetchUserProfile = useCallback(async (userId: string, userEmail?: string): Promise<UserProfile | null> => {
    // Petit helper pour normaliser la donnée renvoyée (jointure job_function -> label).
    const normalize = (row: any | null): UserProfile | null => {
      if (!row) return null;
      const jf = row.job_functions as { label: string | null } | null | undefined;
      return {
        ...row,
        job_function_label: jf?.label ?? null,
      } as UserProfile;
    };

    const SELECT = "id, email, full_name, first_name, last_name, company_name, store_id, department_id, enterprise_id, region_id, centrale_id, supplier_title, is_active, trial_expires_at, created_via, job_function_id, job_title, supplier_account_type, data_consent_accepted_at, data_consent_version, data_consent_source, job_functions(label)";

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select(SELECT)
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("[AuthContext] Profile fetch error:", error);
        return null;
      }

      if (data) return normalize(data);

      // Auto-create fallback profile.
      // IMPORTANT: do NOT use the email local-part as a display name.
      // We deliberately leave first_name/last_name empty and let `full_name`
      // hold the neutral "Utilisateur" placeholder. The UI uses
      // `getUserDisplayName()` which falls back to "Utilisateur".
      const email = userEmail || "unknown@unknown.com";
      console.warn("[AuthContext] Profile missing — auto-creating for", userId);
      const { data: created, error: upsertError } = await supabase
        .from("profiles")
        .upsert({
          id: userId,
          email,
          full_name: "Utilisateur",
          first_name: null,
          last_name: null,
          company_name: "Non défini",
          is_active: true,
        }, { onConflict: "id" })
        .select(SELECT)
        .maybeSingle();

      if (upsertError) {
        console.error("[AuthContext] Profile upsert failed:", upsertError);
        return null;
      }
      return normalize(created);
    } catch (e) {
      console.error("[AuthContext] Profile fetch exception:", e);
      return null;
    }
  }, []);

  // Core: resolve role + profile for a session
  const resolveAuth = useCallback(async (session: Session) => {
    const userId = session.user.id;
    const start = performance.now();

    try {
      const [role, profile] = await withTimeout(
        Promise.all([fetchUserRole(userId), fetchUserProfile(userId, session.user.email)]),
        8000, // shortened: failing fast lets INITIAL_SESSION retry sooner
        "auth:resolve"
      );

      const ms = Math.round(performance.now() - start);
      console.log(`[AuthContext] resolved in ${ms}ms — role=${role}, profile=${profile ? "ok" : "null"}`);

      // Only update if this user is still the current one
      if (currentUserIdRef.current === userId) {
        setState(prev => ({
          ...prev,
          user: session.user,
          session,
          userRole: role,
          userProfile: profile,
          isLoading: false,
          isAuthenticated: true,
        }));
      }
    } catch (e) {
      const ms = Math.round(performance.now() - start);
      console.error(`[AuthContext] resolveAuth failed after ${ms}ms:`, e);
      if (currentUserIdRef.current === userId) {
        setState(prev => ({
          ...prev,
          user: session.user,
          session,
          userRole: null,
          userProfile: null,
          isLoading: false,
          isAuthenticated: true,
        }));
      }
    }
  }, [fetchUserRole, fetchUserProfile]);

  useEffect(() => {
    let isMounted = true;

    // Track whether we've already resolved for this user to avoid duplicate work
    let resolvedForUser: string | null = null;

    // CRITICAL: Supabase fires onAuthStateChange("SIGNED_IN") during _recoverAndRefresh
    // BEFORE getSession() returns. If we run resolveAuth on that early event, the
    // PostgREST request often fires before the access token is attached → 8-15s timeout.
    // We gate the listener until initSession has produced its first state update.
    let initialSessionDone = false;

    async function initSession() {
      const initStart = performance.now();
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        const restoreMs = Math.round(performance.now() - initStart);
        console.log(`[AuthContext] initial getSession in ${restoreMs}ms — user=${session?.user?.id ?? "none"}`);

        if (session?.user) {
          currentUserIdRef.current = session.user.id;
          resolvedForUser = session.user.id;

          setState(prev => ({
            ...prev,
            user: session.user,
            session,
            isAuthenticated: true,
            isLoading: true,
          }));

          await resolveAuth(session);
        } else {
          if (isMounted) {
            setState(prev => ({
              ...prev,
              user: null, session: null, userRole: null,
              userProfile: null, isLoading: false, isAuthenticated: false,
            }));
          }
        }
      } catch (e) {
        console.error("[AuthContext] getSession failed:", e);
        if (isMounted) {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } finally {
        // Open the gate AFTER initial resolution so the listener stops being noisy.
        initialSessionDone = true;
      }
    }

    // 2. Subscribe to auth changes for subsequent events (sign in/out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        console.log(`[AuthContext] event=${event} initialDone=${initialSessionDone} sameUser=${session?.user?.id === resolvedForUser}`);

        // Logout in progress: signOut() owns the state. Ignore listener events to avoid loops.
        if (isLoggingOutRef.current) {
          console.log("[AuthContext] Ignoring", event, "during logout");
          return;
        }

        // Token refresh: just update session, don't re-fetch
        if (event === "TOKEN_REFRESHED" && session?.user?.id === currentUserIdRef.current) {
          setState(prev => ({ ...prev, session, user: session?.user || null }));
          return;
        }

        // INITIAL_SESSION is owned by initSession() — never act on it here.
        if (event === "INITIAL_SESSION") {
          return;
        }

        // SIGNED_IN that arrives BEFORE initSession completes is a duplicate of
        // the session restore (Supabase emits it during _recoverAndRefresh).
        // Skip it entirely to prevent a token-less PostgREST race that times out.
        if (event === "SIGNED_IN" && !initialSessionDone) {
          console.log("[AuthContext] Skipping early SIGNED_IN (initial restore in flight)");
          return;
        }

        // Signed out (SIGNED_OUT or session became null)
        if (!session?.user) {
          currentUserIdRef.current = null;
          resolvedForUser = null;
          setState(prev => ({
            ...prev,
            user: null, session: null, userRole: null,
            userProfile: null, isLoading: false, isAuthenticated: false,
          }));
          return;
        }

        // Skip if we already resolved for this exact user (duplicate SIGNED_IN, USER_UPDATED, etc.)
        if (resolvedForUser === session.user.id) {
          // Just keep session/user fresh, never re-fetch role/profile.
          setState(prev =>
            prev.session?.access_token === session.access_token
              ? prev
              : { ...prev, session, user: session.user }
          );
          return;
        }

        // New user sign-in: set loading + resolve
        currentUserIdRef.current = session.user.id;
        resolvedForUser = session.user.id;

        setState(prev => ({
          ...prev,
          user: session.user,
          session,
          isAuthenticated: true,
          isLoading: true,
        }));

        await resolveAuth(session);
      }
    );

    // Start session restoration
    initSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [resolveAuth]);

  const signOut = useCallback(async () => {
    // Anti-loop guard: ignore double clicks
    if (isLoggingOutRef.current) {
      console.log("[Logout] Already in progress — ignoring");
      return;
    }
    isLoggingOutRef.current = true;
    const logoutStart = performance.now();
    console.log("[Logout] Start");

    // Mark logging out so RouteGuard / effects skip redirects
    setState(prev => ({ ...prev, isLoggingOut: true }));

    // Mark logout-in-progress so the /auth page never auto-redirects on the
    // next full page load (defends against any stale token GoTrue might
    // resurrect from storage during _recoverAndRefresh).
    try {
      sessionStorage.setItem("lyko_logging_out", "1");
    } catch {
      // sessionStorage unavailable — best effort
    }

    try {
      // Local sign-out only: clears the in-memory session and removes the token
      // from localStorage. Always works even if the server session is gone.
      // We deliberately skip scope:"global" because:
      //   - the access token may already be expired → 403 session_not_found
      //   - the refresh token gets invalidated server-side at next refresh anyway
      //   - it pollutes auth logs with errors that look like real bugs
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) console.warn("[Logout] local signOut error (ignored):", error.message);
    } catch (err) {
      console.warn("[Logout] signOut threw (ignored):", err);
    }

    // Hard purge any residual Supabase auth keys from storage so the next
    // page load cannot rehydrate a stale session from localStorage.
    try {
      const purge = (storage: Storage) => {
        const keys: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k && (k.startsWith("sb-") || k.startsWith("supabase.auth."))) {
            keys.push(k);
          }
        }
        keys.forEach((k) => storage.removeItem(k));
      };
      purge(localStorage);
      purge(sessionStorage);
    } catch (err) {
      console.warn("[Logout] storage purge failed:", err);
    }

    // Clear local auth state immediately (don't wait for onAuthStateChange race)
    currentUserIdRef.current = null;
    setState({
      user: null,
      session: null,
      userRole: null,
      userProfile: null,
      isLoading: false,
      isAuthenticated: false,
      isLoggingOut: true, // keep true until navigation completes
    });

    // Wipe React Query cache (prevents stale user-scoped queries from refetching as anon)
    try {
      queryClient.cancelQueries();
      queryClient.clear();
    } catch (err) {
      console.warn("[Logout] queryClient.clear failed:", err);
    }

    // Single, definitive redirect — bypass React Router to guarantee a clean tree
    // (avoids any concurrent navigate() in mounted pages from racing).
    const totalMs = Math.round(performance.now() - logoutStart);
    console.log(`[Logout] Done in ${totalMs}ms — redirecting to /auth`);
    window.location.replace("/auth");
  }, [queryClient]);

  const refreshAuth = useCallback(async () => {
    if (state.session) {
      await resolveAuth(state.session);
    }
  }, [state.session, resolveAuth]);

  const value = useMemo(
    () => ({ ...state, signOut, refreshAuth }),
    [state, signOut, refreshAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Legacy hook compatibility
export function useAuthRole() {
  const { userRole, user, userProfile, isLoading } = useAuth();
  return {
    userRole,
    userId: user?.id || null,
    userProfile: userProfile ? { enterprise_id: userProfile.enterprise_id, store_id: userProfile.store_id } : null,
    isLoading,
  };
}